import React, { useState, useRef } from "react";
import { IReducer } from "../../state/reducer";
import {
  downloadSaveFile,
  uploadSaveFile,
  validateSaveData,
} from "../../utils/saveLoad";
import "./Settings.css";

interface SettingsProps extends IReducer {
  onResetGame: () => void;
}

const Settings: React.FC<SettingsProps> = ({
  state,
  dispatch,
  onResetGame,
}) => {
  const [message, setMessage] = useState<string>("");
  const [messageType, setMessageType] = useState<"success" | "error" | null>(
    null,
  );
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const showMessage = (msg: string, type: "success" | "error") => {
    setMessage(msg);
    setMessageType(type);
    const duration = type === "error" ? 15000 : 3000;
    setTimeout(() => {
      setMessage("");
      setMessageType(null);
    }, duration);
  };

  const handleDownload = () => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    const filename = `brickpacked_save_${timestamp}.json`;
    downloadSaveFile(state, filename);
    showMessage("Save file downloaded successfully!", "success");
  };

  const handleManualSave = () => {
    dispatch({ type: "SAVE_GAME" });
    showMessage("Game saved to browser storage!", "success");
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const saveData = await uploadSaveFile(file);

    // First check if file was parsed successfully
    if (!saveData) {
      showMessage("Invalid Save Format", "error");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // Validate save data structure
    const validationError = validateSaveData(saveData);
    if (validationError) {
      showMessage("Invalid Save Format", "error");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // All validation passed - load the save
    dispatch({ type: "LOAD_GAME", saveData });
    showMessage("Save file loaded successfully!", "success");

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleResetConfirm = () => {
    setShowResetConfirmation(false);
    // Call the reset handler from parent - it will clear auto-save, clear localStorage, and reload
    onResetGame();
  };

  return (
    <div className="settingsViewWrapper">
      <div className="settingsViewContainer">
        <div className="settingsViewHeader">Settings</div>
        <div className="settingsViewContent">
          {/* Save/Load Section */}
          <div className="settingsSection">
            <div
              style={{ fontSize: "12px", color: "#666", textAlign: "center" }}
            >
              Auto-save runs every 15 seconds and writes to your browser's local
              storage (cookies)
            </div>
            <div className="settingsSectionTitle">Save & Load</div>

            <div className="settingsButtonGroup">
              <button
                className="settingsButton settingsButtonPrimary"
                onClick={handleManualSave}
              >
                Manual Save
              </button>
              <button className="settingsButton" onClick={handleDownload}>
                Download Save
              </button>
              <div className="fileInputContainer">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  className="fileInput"
                  id="file-upload"
                  onChange={handleUpload}
                />
                <label htmlFor="file-upload" className="fileInputLabel">
                  Upload Save
                </label>
              </div>
            </div>
          </div>

          {/* Reset Section */}
          <div className="settingsSection">
            <div className="settingsSectionTitle">Danger Zone</div>
            {!showResetConfirmation ? (
              <button
                className="settingsButton settingsButtonDanger"
                onClick={() => setShowResetConfirmation(true)}
              >
                Reset Game
              </button>
            ) : (
              <div className="resetConfirmation">
                <div className="resetConfirmationText">
                  Are you sure? This will delete all progress and cannot be
                  undone!
                </div>
                <div className="resetConfirmationButtons">
                  <button
                    className="settingsButton settingsButtonDanger"
                    onClick={handleResetConfirm}
                  >
                    Yes, Reset Everything
                  </button>
                  <button
                    className="settingsButton"
                    onClick={() => setShowResetConfirmation(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Message Display */}
          {message && (
            <div
              className={
                messageType === "success" ? "saveMessage" : "errorMessage"
              }
            >
              {message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
